/**
 * Artwork readability -- real ink-vs-garment contrast (#2791).
 *
 * Replaces a badge that was hardcoded `ok: true` in two components
 * (VariantCard, RoutingStrip), with a ratio picked from the variant kind
 * (15.8 / 12.1 / 4.5). Because it read neither the artwork nor the
 * garment it could not fail, and the reader most likely to trust it is
 * the one about to put dark ink on a dark garment.
 *
 * WHERE THE INK COLOR COMES FROM, AND HOW RELIABLE IT IS
 * -----------------------------------------------------
 * The ink color is the operator-declared ``ink_color_hex`` -- a
 * validated ``#RRGGBB`` column on BOTH ``artworks`` (Artwork.
 * ink_color_hex, api/src/models/artwork.py) and ``variants``
 * (Variant.ink_color_hex). The Artwork value wins when a real Artwork
 * row is resolved; the variant column is the legacy fallback. Both are
 * NULLABLE and are legitimately null in seeded data, so the ink color is
 * KNOWN-OR-ABSENT, never guessed.
 *
 * That nullability is the whole reason this module returns ``null``
 * instead of a verdict. VariantCard's display-only ``inkHex`` computed
 * falls back to ``#ffffff`` so the ink swatch always renders something;
 * feeding THAT into a contrast check would manufacture a confident
 * verdict out of a placeholder (white ink scores well on most garments),
 * which is the original defect wearing better clothes. Callers must pass
 * the un-fallen-back value.
 *
 * A stronger source exists: ``Artwork.dominant_ink_hex`` is sampled from
 * the actual image pixels (src/services/artwork_luminance.py, ADR 0062).
 * It IS serialized (the SAFRS ``/api/artworks/`` row, the library list
 * and detail payloads, the store decoration rollup) and already drives
 * the off-product art backdrop (``@/utils/artBackdrop``). It is not yet
 * the input here; switching is a change at the call sites that choose
 * ``inkHex`` -- it describes the artwork rather than what someone typed
 * about it, but it is an AVERAGE, so a multi-ink logo reads as one blend.
 *
 * The garment color is the product's own selected color set, which
 * already carries ``{color_sku, name, hex}`` and already drives
 * light/dark routing via ``classifyLDWithName``.
 *
 * WCAG math is NOT reimplemented here: ``contrastRatio`` in
 * ``@/utils/luminance`` is the single existing implementation (its own
 * docstring already anticipates "artwork-vs-garment-color") and both
 * components now share this one module rather than keeping two copies.
 */
import { classifyLDWithName, contrastRatio } from '@/utils/luminance';
import {
    VARIANT_KIND_CATCH_ALL_DARK,
    VARIANT_KIND_CATCH_ALL_LIGHT,
    VARIANT_KIND_SPECIFIC_COLORS,
} from '@/utils/decorationRouting';

/**
 * Minimum acceptable ink-vs-garment contrast ratio.
 *
 * WCAG 2.x text-contrast level AA. Decoration artwork is closer to a
 * graphic than to body text (the non-text rule would allow 3:1), but a
 * decorated garment is read across a room rather than on a backlit
 * screen, so we hold the stricter line rather than the permissive one.
 *
 * The pre-existing TODO in VariantCard anticipated making this
 * configurable (3.0 / 4.5 / 7.0) via a config-over-code setting. That
 * remains the right end state and is deliberately NOT built here -- one
 * honest constant is a smaller, checkable change than a settings surface
 * with no reader.
 */
export const READABILITY_MIN_RATIO = 4.5;

/**
 * Normalize a caller-supplied hex to a usable string, or null.
 *
 * Blank/whitespace strings and non-strings are treated as "unknown"
 * rather than coerced -- see the module docstring on why a fallback
 * color must never reach the contrast math.
 */
function usableHex(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Select the garment colors a variant actually prints on.
 *
 * Mirrors the resolver order the "Routes: ..." subline already uses
 * (VariantCard routesSublineBuild, itself mirroring the backend
 * api/src/endpoints/decoration_variants.py:variant_for_color), so the
 * badge judges exactly the colors the card says it routes to. Light/dark
 * classification goes through the same ``classifyLDWithName`` pipeline
 * the filmstrip routes with -- a second notion of "dark" here would let
 * the badge and the routing disagree.
 *
 * @param {{kind?: string, color_skus?: string[]}|null} variant
 * @param {Array<{color_sku?: string, name?: string, hex?: string|null}>|null}
 *   productColors  The product's full selected color set.
 * @returns {Array<object>} The subset this variant prints on. A variant
 *   with no recognized kind routes to nothing.
 */
export function routedColorsFor(variant, productColors) {
    const colors = Array.isArray(productColors) ? productColors : [];
    const kind = variant?.kind ?? null;
    if (!kind || colors.length === 0) return [];

    if (kind === VARIANT_KIND_SPECIFIC_COLORS) {
        const skus = Array.isArray(variant?.color_skus) ? variant.color_skus : [];
        const wanted = new Set(skus);
        return colors.filter((c) => wanted.has(c?.color_sku));
    }

    if (
        kind === VARIANT_KIND_CATCH_ALL_LIGHT ||
        kind === VARIANT_KIND_CATCH_ALL_DARK
    ) {
        const want =
            kind === VARIANT_KIND_CATCH_ALL_LIGHT ? 'light' : 'dark';
        return colors.filter((c) => {
            if (!c || typeof c !== 'object') return false;
            const { ld } = classifyLDWithName({
                hex: c.hex ?? null,
                name: c.name ?? null,
            });
            return ld === want;
        });
    }

    // single_catch_all (and any future catch-all kind): every color.
    return colors;
}

/**
 * Compute the readability verdict for one ink color over a set of
 * garment colors.
 *
 * Reports the WORST color in the routed set: a variant that prints on
 * both white and black must warn, because the badge speaks for every
 * garment the artwork actually lands on, not the most flattering one.
 *
 * @param {object}   args
 * @param {string|null} args.inkHex  Operator-declared ink color
 *   (``#RRGGBB``). MUST be the un-fallen-back value -- pass null when
 *   unknown rather than a display placeholder.
 * @param {Array<{color_sku?: string, name?: string, hex?: string|null}>|null}
 *   args.garmentColors  The garment colors this artwork routes to.
 * @returns {{ok: boolean, label: string, ratio: number,
 *            worstColorName: string|null}|null}
 *   ``null`` when the verdict is genuinely unknowable (no ink color, or
 *   no garment color with a usable hex). Callers render NOTHING for
 *   null -- never a green badge.
 */
export function readabilityFor({ inkHex, garmentColors } = {}) {
    const ink = usableHex(inkHex);
    if (ink === null) return null;

    const colors = Array.isArray(garmentColors) ? garmentColors : [];
    if (colors.length === 0) return null;

    let worstRatio = null;
    let worstColor = null;

    for (const color of colors) {
        const garmentHex = usableHex(color?.hex);
        if (garmentHex === null) continue;

        // contrastRatio returns null for an unparseable hex; skip those
        // colors rather than letting one bad row decide the verdict.
        const ratio = contrastRatio(ink, garmentHex);
        if (ratio === null) continue;

        if (worstRatio === null || ratio < worstRatio) {
            worstRatio = ratio;
            worstColor = color;
        }
    }

    // Every garment color was unusable -- the ink is known but there is
    // nothing to judge it against.
    if (worstRatio === null) return null;

    const ok = worstRatio >= READABILITY_MIN_RATIO;

    return {
        ok,
        label: ok ? 'Readable' : 'Low contrast',
        ratio: worstRatio,
        worstColorName: worstColor?.name || worstColor?.color_sku || null,
    };
}
